import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  TextField,
} from "@mui/material";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import { manageMeasures } from "./utils";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { clone, isEmpty, isNil } from "ramda";
import { ErrorText } from "./styled";
import { getMuiTheme, validateEmail, validatePassword } from "../../../../utils/utils";
import { ThemeProvider } from "styled-components";
import palette from "../../../../../config/colorPalette";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const AssetInfoForm = ({
  assetName,
  setAssetName,
  zoom,
  setZoom,
  demarcation,
  setDemarcation,
  avatar,
  setAvatar,
  equipment,
  setEquipment,
  equipmentCollection,
  setEquipmentCollection,
  measures,
  setMeasures,
  measuresComputed,
  setMeasuresComputed,
  measuresAlerts,
  setMeasuresAlerts,
  thresholdsValues,
  setThresholdsValues,
  errors,
  setErrors,
  alertTimeRange,
  alertTimeMeasure,
  setAlertTimeRange,
  setAlertTimeMeasure,
  URL,
  setURL,
  zones,
  setZones,
  thresholdsRanges,
  setThresholdsRanges,
  objectMeasures,
  configData,
  assetModules,
  setAssetModules,
  equipmentTypes,
  assetMeasurands,
  setAssetMeasurands,
  assetExternalServices,
  setAssetExternalServices,
  assetAdditionalSensors,
  setAssetAdditionalSensors,
  assetExternalServicesCheck,
  setAssetExternalServicesCheck,
  assetExternalServicesFields,
  setAssetExternalServicesFields,
  setMoreSubsAllowed,
}) => {
  const intl = useIntl();
  const theme = useMemo(() => getMuiTheme(), []);
  const equipmentOptions = ["DTS", "DTSS", "DAS"];
  //const moduleOptions = ["thermic", "dam", "railway", "marine", "road", "intrusion"];
  const prevEquipmentCollection = useRef(equipmentCollection);
  const [showOldPassword, setShowOldPassword] = useState(false);

  //const [computing, setComputings] = useState([]);

  useEffect(() => {
    if (assetName.length < 3) {
      setErrors((prevErrors) => ({ ...prevErrors, "asset-name": "3.characters" }));
    } else {
      setErrors((prevErrors) => {
        const { "asset-name": assetName, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  }, [assetName, setErrors]);

  useEffect(() => {
    if (prevEquipmentCollection.current !== equipmentCollection) {
      console.log(equipment);
      manageMeasures({
        equipmentCollection,
        setEquipmentCollection,
        equipment,
        setEquipment,
        thresholdsValues,
        setThresholdsValues,
        measures,
        setMeasures,
        measuresComputed,
        setMeasuresComputed,
        measuresAlerts,
        setMeasuresAlerts,
        objectMeasures,
        zones,
        setZones,
        thresholdsRanges,
        setThresholdsRanges,
      });
      prevEquipmentCollection.current = equipmentCollection;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentCollection]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ThemeProvider theme={theme}>
        <Grid container spacing={10} direction="row" justifyContent="center" alignItems="flex-start">
          <Grid item xs={10} sm={2}>
            <FormControl>
              <TextField
                autoFocus
                value={assetName}
                variant="outlined"
                autoComplete="off"
                margin="dense"
                id="asset-name"
                label={intl.formatMessage({ id: "name" })}
                type="text"
                fullWidth
                required
                error={errors["asset-name"] ? true : false}
                onChange={(e) => {
                  setAssetName(e.target.value);
                  let newEquipment = clone(equipment);
                  for (let index = 0; index < newEquipment.length; index++) {
                    newEquipment[index].asset = e.target.value;
                  }
                  setEquipment(newEquipment);
                }}
              ></TextField>
              {!isNil(errors["asset-name"]) && (
                <ErrorText>{intl.formatMessage({ id: errors["asset-name"] })}</ErrorText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={10} sm={2}>
            <FormControl>
              <TextField
                autoFocus
                value={URL}
                variant="outlined"
                autoComplete="off"
                margin="dense"
                id="url"
                label={intl.formatMessage({ id: "url-microservice" })}
                type="text"
                fullWidth
                required
                error={errors["url"] ? true : false}
                onChange={(e) => {
                  setURL(e.target.value);
                }}
              ></TextField>
              {!isNil(errors["url"]) && <ErrorText>{intl.formatMessage({ id: errors["url"] })}</ErrorText>}
            </FormControl>
          </Grid>
          <Grid item xs={10} sm={1}>
            <TextField
              id="zoom"
              margin="dense"
              style={{ width: "5rem" }}
              variant="outlined"
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
              label={"Zoom"}
              required
              type="number"
              inputProps={{ min: 0, max: 16 }}
            ></TextField>
          </Grid>
          <Grid item xs={10} sm={1}>
            <div style={{ marginTop: "10%" }}>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                id="file-input"
                onChange={(event) => {
                  const file = event.target.files[0];
                  if (file && file.type.startsWith("image/")) {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const base64String = reader.result;
                      setAvatar(base64String.split("base64,")?.[1]);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <label htmlFor="file-input">
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: avatar.length === 0 ? palette.primary : "#420d10",
                    color: avatar.length > 0 && "grey",
                  }}
                  component="span"
                >
                  upload image {avatar.length > 0 && <CheckIcon />}
                </Button>
              </label>
            </div>
          </Grid>
          {!isNil(configData) && !isNil(configData.demarcations) && !isEmpty(configData.demarcations) ? (
            <Grid item xs={12} sm={1.5}>
              <TextField
                id="demarcation"
                margin="dense"
                sx={{ width: "10rem" }}
                variant="outlined"
                value={demarcation}
                onChange={(e) => setDemarcation(e.target.value)}
                select
                label={intl.formatMessage({ id: "demarcation" })}
                required
              >
                {configData.demarcations.map((dem, demIndex) => {
                  return (
                    <MenuItem key={`dem_${dem}_${demIndex}`} value={dem}>
                      {dem}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
          ) : null}

          <Grid item xs={10} sm={3}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "0.5rem" }}>
              <TextField
                id={"alert_time_range"}
                style={{ marginTop: "0.2rem" }}
                value={alertTimeRange}
                onChange={(e) => {
                  const newErrors = { ...errors };
                  if (isEmpty(e.target.value)) {
                    newErrors[e.target.id] = "not.empty";
                  } else {
                    delete newErrors[e.target.id];
                  }
                  setErrors(newErrors);
                  setAlertTimeRange(e.target.value);
                }}
                type="number"
                variant="outlined"
                error={errors["alert_time_range"] ? true : false}
                label={intl.formatMessage({ id: "alert.time.range" })}
                required
              ></TextField>
              {!isNil(errors["alert_time_range"]) && (
                <ErrorText>{intl.formatMessage({ id: errors["alert_time_range"] })}</ErrorText>
              )}
              <TextField
                id="alert_time_measure"
                margin="dense"
                style={{ width: "14rem" }}
                variant="outlined"
                value={alertTimeMeasure}
                defaultValue={1}
                error={errors["alert_time_measure"] ? true : false}
                onChange={(e) => {
                  setAlertTimeMeasure(parseInt(e.target.value));
                }}
                select
                label={intl.formatMessage({ id: "alert.time.measure" })}
                required
              >
                <MenuItem value={1}>{intl.formatMessage({ id: "hours" })}</MenuItem>
                <MenuItem value={24}>{intl.formatMessage({ id: "days" })}</MenuItem>
              </TextField>
            </div>
          </Grid>
        </Grid>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0 0.7rem" }}>
          <div style={{ fontSize: "large", marginTop: "0.5rem", width: "7rem" }}>
            <div style={{ minWidth: "10rem", margin: "2rem" }}>{intl.formatMessage({ id: "equipments" })}:</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.5rem" }}>
            {equipmentCollection.map((et, index) => (
              <>
                <TextField
                  id={`type${index}`}
                  key={`type${index}`}
                  margin="dense"
                  style={{ width: "15rem" }}
                  variant="outlined"
                  value={et}
                  onChange={(e) => {
                    let newTypes = clone(equipmentCollection);
                    newTypes[index] = e.target.value;

                    let newMeasurands = [];
                    let newModules = [];

                    newTypes.forEach((typeName) => {
                      const typeData = equipmentTypes.find((et) => et.name === typeName);
                      if (typeData) {
                        if (typeData.measurands) {
                          typeData.measurands.forEach((msr) => {
                            if (!newMeasurands.some((existing) => existing === msr)) {
                              newMeasurands.push(msr);
                            }
                          });
                        }

                        //if (typeData.config?.modules) {
                        //  typeData.config.modules.forEach((mod) => {
                        //    if (!newModules.some((existing) => existing.name === mod.name)) {
                        //      newModules.push(mod);
                        //    }
                        //  });
                        //}
                      }
                    });

                    setAssetModules(newModules);
                    setAssetMeasurands(newMeasurands);
                    setEquipmentCollection(newTypes);
                  }}
                  select
                  label={intl.formatMessage({ id: "select.type.equipment" })}
                  required
                >
                  {equipmentOptions.map((eo, index2) => (
                    <MenuItem value={eo}>{eo}</MenuItem>
                  ))}
                </TextField>
              </>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            {equipmentCollection.length < 6 && (
              <IconButton
                style={{ backgroundColor: palette.dividerLight }}
                onClick={() => {
                  setEquipmentCollection((et) => [...et, ""]);
                  let newZones = clone(zones);
                  newZones[`equipment${equipmentCollection.length + 1}`] = {
                    channel1: {
                      sub1: [{ ini: 0, end: 10, alertsConfigurations: null }],
                    },
                  };
                  setZones(newZones);
                }}
              >
                <AddIcon sx={{ color: palette.primary }} />
              </IconButton>
            )}
            {equipmentCollection.length > 1 && (
              <DeleteForeverIcon
                onClick={() => {
                  const newET = clone(equipmentCollection);
                  const equipmentData = equipmentTypes.find(
                    (et) => equipmentCollection[equipmentCollection.length - 1] === et.name,
                  );
                  if (!isNil(equipmentData)) {
                    // const actualModules = configData.modules;
                    const actualMeasurands = equipmentData.measurands;
                    let newMeasurands = clone(assetMeasurands);
                    //let newModules = clone(assetModules);

                    // Suponiendo que la propiedad única es `ref`
                    //const key = "ref";
                    //const key2 = "name";

                    //const makeKeySet = (arr, key) => new Set(arr.map((item) => item?.[key]).filter(Boolean));
                    //const makeKeySet2 = (arr, key) => new Set(arr.map((item) => item?.[key]).filter(Boolean));

                    //const actualModulesSet = makeKeySet2(actualModules, key2);
                    //const actualMeasurandsSet = makeKeySet(actualMeasurands, key);

                    //newModules = newModules.filter((m) => !actualModulesSet.has(m?.[key2]));
                    //newMeasurands = newMeasurands.filter((m) => !actualMeasurandsSet.has(m?.[key]));
                    const makeSet = (arr) => new Set(arr.filter(Boolean)); // crea un set de valores únicos

                    const actualMeasurandsSet = makeSet(actualMeasurands);

                    newMeasurands = newMeasurands.filter((m) => !actualMeasurandsSet.has(m));
                    setAssetMeasurands(newMeasurands);
                    //setAssetModules(newModules);
                  }
                  newET.splice(1, 1);
                  setEquipmentCollection(newET);
                  let newZones = clone(zones);
                  delete newZones[`equipment${equipmentCollection.length}`];
                  setZones(newZones);
                }}
                sx={{ color: palette.primary, cursor: "pointer" }}
              />
            )}
          </div>
        </div>
        <div style={{ display: "inline-block", marginLeft: "8.7rem" }}>
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  color: palette.primary,
                  "&.Mui-checked": {
                    color: palette.primary,
                  },
                }}
                checked={assetAdditionalSensors}
                onChange={(event) => {
                  setAssetAdditionalSensors(event.target.checked);
                }}
              />
            }
            label={intl.formatMessage({ id: "additional.sensors" })}
          />
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  color: palette.primary,
                  "&.Mui-checked": {
                    color: palette.primary,
                  },
                }}
                checked={assetExternalServicesCheck}
                onChange={(event) => {
                  setAssetExternalServicesCheck(event.target.checked);
                }}
              />
            }
            label={intl.formatMessage({ id: "external.services" })}
          />
          {equipmentCollection[0] !== "" &&
            (() => {
              const measurandOptions = [];
              //const moduleOptions = [];
              for (let indexColletion = 0; indexColletion < equipmentCollection.length; indexColletion++) {
                const equipmentType = equipmentCollection[indexColletion];
                if (equipmentType !== "") {
                  const equipmentData = equipmentTypes.find((et) => equipmentType === et.name);
                  //for (let indexModule = 0; indexModule < configData.modules.length; indexModule++) {
                  //  if (!moduleOptions.some((opt) => opt.name === configData.modules[indexModule].name))
                  //    moduleOptions.push(configData.modules[indexModule]);
                  //}
                  for (let indexMeasurand = 0; indexMeasurand < equipmentData.measurands.length; indexMeasurand++) {
                    if (!measurandOptions.includes(equipmentData.measurands[indexMeasurand])) {
                      measurandOptions.push(equipmentData.measurands[indexMeasurand]);
                    }
                  }
                }
              }

              return (
                <>
                  <Autocomplete
                    multiple
                    id="measurand_select"
                    variant="outlined"
                    options={measurandOptions}
                    value={assetMeasurands}
                    isOptionEqualToValue={(option, value) => option === value}
                    sx={{
                      width: "16rem", // aumenta el ancho para que quepan más chips
                      ".MuiChip-root": {
                        maxWidth: "100%", // permite que los chips no se corten
                      },
                      ".MuiAutocomplete-tag": {
                        maxWidth: "calc(100% - 16px)", // previene overflow
                      },
                      marginTop: "4%",
                    }}
                    disableCloseOnSelect
                    onChange={(event, values) => {
                      setAssetMeasurands(values);
                    }}
                    getOptionLabel={(option) => intl.formatMessage({ id: option })}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {intl.formatMessage({ id: option })}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" label={intl.formatMessage({ id: "measurands" })} />
                    )}
                    required
                  />
                  {/*<Autocomplete
                    multiple
                    id="module_select"
                    variant="outlined"
                    value={assetModules}
                    options={moduleOptions}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    sx={{
                      width: "16rem", // aumenta el ancho para que quepan más chips
                      ".MuiChip-root": {
                        maxWidth: "100%", // permite que los chips no se corten
                      },
                      ".MuiAutocomplete-tag": {
                        maxWidth: "calc(100% - 16px)", // previene overflow
                      },
                      marginTop: "4%",
                    }}
                    disableCloseOnSelect
                    onChange={(event, values) => {
                      for (let indexValue = 0; indexValue < values.length; indexValue++) {
                        const actualModule = configData.modules.find(
                          (module) => module.name === values[indexValue].name,
                        );
                        const inclinometersFound = actualModule.packages.find((pack) => pack.name === "inclinometers");

                        if (inclinometersFound) {
                          setMoreSubsAllowed(true);
                          break;
                        }
                        setMoreSubsAllowed(false);
                      }
                      setAssetModules(values);
                    }}
                    getOptionLabel={(option) => intl.formatMessage({ id: option.name })}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {intl.formatMessage({ id: option.name })}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" label={intl.formatMessage({ id: "modules" })} />
                    )}
                    required
                  />*/}
                </>
              );
            })()}
          {assetExternalServicesCheck &&
            (() => {
              const serviceList = configData.externalServices.services;
              if (!configData.externalServices.contracted) {
                return null;
              }
              const serviceOptions = [];
              for (let indexService = 0; indexService < serviceList.length; indexService++) {
                const hayCoincidencia = equipmentCollection.some((item) =>
                  serviceList[indexService].equipment_types_allowed.includes(item),
                );
                if (serviceList[indexService].active && hayCoincidencia) {
                  serviceOptions.push(serviceList[indexService].name);
                }
              }
              if (isEmpty(serviceOptions)) {
                return null;
              }

              return (
                <>
                  <Autocomplete
                    multiple
                    id="service_select"
                    variant="outlined"
                    options={serviceOptions}
                    sx={{
                      width: "16rem", // aumenta el ancho para que quepan más chips
                      ".MuiChip-root": {
                        maxWidth: "100%", // permite que los chips no se corten
                      },
                      ".MuiAutocomplete-tag": {
                        maxWidth: "calc(100% - 16px)", // previene overflow
                      },
                      marginTop: "4%",
                    }}
                    disableCloseOnSelect
                    onChange={(event, values) => {
                      let newServicesFields = clone(assetExternalServicesFields);
                      let newServicesValues = clone(assetExternalServices);

                      newServicesFields = newServicesFields.filter((sf) => values.includes(sf.name));

                      const remainingFieldNames = newServicesFields.flatMap((sf) => sf.config_form.map((f) => f.name));
                      newServicesValues = newServicesValues.filter((sv) => remainingFieldNames.includes(sv.ref));

                      for (let indexValue = 0; indexValue < values.length; indexValue++) {
                        const service = serviceList.find((sl) => sl.name === values[indexValue]);
                        if (!newServicesFields.some((sf) => sf.name === service.name)) {
                          newServicesFields.push(service);
                        }

                        for (const field of service.config_form) {
                          const found = newServicesValues.find((sv) => sv.ref === field.name);
                          if (!found) {
                            const newObject = {
                              ref: field.name,
                              value: ["text", "mail", "password"].includes(field.type)
                                ? ""
                                : field.type === "number"
                                ? 0
                                : field.options[0],
                            };
                            newServicesValues.push(newObject);
                          }
                        }
                      }

                      if (isEmpty(values)) {
                        newServicesFields = [];
                        newServicesValues = [];
                      }

                      setAssetExternalServicesFields(newServicesFields);
                      setAssetExternalServices(newServicesValues);
                    }}
                    getOptionLabel={(option) => intl.formatMessage({ id: option })}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {intl.formatMessage({ id: option })}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={intl.formatMessage({ id: "external.services" })}
                      />
                    )}
                    required
                  />
                </>
              );
            })()}
          {assetExternalServicesFields.map((aesf, aesfIndex) => {
            const elements = [];
            for (let index = 0; index < aesf.config_form.length; index++) {
              if (!aesf.config_form[index].required) continue; // OPCIONES: TEXT, NUMBER, MAIL, PASSWORD Y SELECT
              if (aesf.config_form[index].type === "text") {
                const actualValue = assetExternalServices.find((aes) => aes.ref === aesf.config_form[index].name);
                elements.push(
                  <>
                    <TextField
                      autoFocus
                      value={actualValue.value}
                      variant="outlined"
                      autoComplete="off"
                      margin="dense"
                      id={aesf.config_form[index].name}
                      key={`${aesf.config_form[index].name}_${aesfIndex}`}
                      label={aesf.name + " " + intl.formatMessage({ id: aesf.config_form[index].name })}
                      type="text"
                      fullWidth
                      required
                      error={errors[aesf.config_form[index].name] ? true : false}
                      onChange={(e) => {
                        let newServiceValues = clone(assetExternalServices);
                        let newIndex = assetExternalServices.findIndex(
                          (aes) => aes.ref === aesf.config_form[index].name,
                        );
                        newServiceValues[newIndex].value = e.target.value;
                        setAssetExternalServices(newServiceValues);
                      }}
                    ></TextField>
                    {!isNil(errors[aesf.config_form[index].name]) && (
                      <ErrorText>{intl.formatMessage({ id: errors[aesf.config_form[index].name] })}</ErrorText>
                    )}
                  </>,
                );
              }
              if (aesf.config_form[index].type === "number") {
                const actualValue = assetExternalServices.find((aes) => aes.ref === aesf.config_form[index].name);

                elements.push(
                  <>
                    <TextField
                      id={aesf.config_form[index].name}
                      key={`${aesf.config_form[index].name}_${aesfIndex}`}
                      style={{ margin: "0 0.2rem 0.8rem", width: "15rem" }}
                      value={actualValue.value}
                      inputProps={{ min: 1 }}
                      onChange={(e) => {
                        let newServiceValues = clone(assetExternalServices);
                        let newIndex = assetExternalServices.findIndex(
                          (aes) => aes.ref === aesf.config_form[index].name,
                        );
                        newServiceValues[newIndex].value = parseInt(e.target.value);
                        setAssetExternalServices(newServiceValues);
                      }}
                      type="number"
                      error={errors[`sub_sectioncount_${0}`] ? true : false}
                      variant="outlined"
                      label={aesf.name + " " + intl.formatMessage({ id: aesf.config_form[index].name })}
                      required
                    ></TextField>
                  </>,
                );
              }
              if (aesf.config_form[index].type === "mail") {
                const actualValue = assetExternalServices.find((aes) => aes.ref === aesf.config_form[index].name);

                elements.push(
                  <>
                    <InputLabel htmlFor={aesf.config_form[index].name}>
                      {intl.formatMessage({ id: "email" })}*
                    </InputLabel>
                    <OutlinedInput
                      id={aesf.config_form[index].name}
                      key={`${aesf.config_form[index].name}_${aesfIndex}`}
                      type="email"
                      required
                      autoComplete="off"
                      endAdornment={
                        <InputAdornment position="end">
                          <AlternateEmailIcon color="#e4e6ef" />
                        </InputAdornment>
                      }
                      label={`${intl.formatMessage({ id: "email" })}*`}
                      value={actualValue.value}
                      onChange={(e) => {
                        let newServiceValues = clone(assetExternalServices);
                        let newIndex = assetExternalServices.findIndex(
                          (aes) => aes.ref === aesf.config_form[index].name,
                        );
                        newServiceValues[newIndex].value = e.target.value;
                        const isValid = validateEmail(e.target.value);
                        setErrors(!isValid ? "email.verification.error.message" : null);
                        setAssetExternalServices(newServiceValues);
                      }}
                    />
                    {!isNil(errors[aesf.config_form[index].name]) && (
                      <ErrorText>{intl.formatMessage({ id: errors[aesf.config_form[index].name] })}</ErrorText>
                    )}
                  </>,
                );
              }
              if (aesf.config_form[index].type === "password") {
                const actualValue = assetExternalServices.find((aes) => aes.ref === aesf.config_form[index].name);

                elements.push(
                  <>
                    <OutlinedInput
                      id="currentPassword"
                      type={showOldPassword ? "text" : "password"}
                      autoComplete="new-password"
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="visibility"
                            onClick={() => setShowOldPassword(!showOldPassword)}
                            onMouseDown={(event) => event.preventDefault()}
                            edge="end"
                            data-testid={`user-profile-visibility`}
                          >
                            {showOldPassword ? <VisibilityOff color="#e4e6ef" /> : <Visibility color="#e4e6ef" />}
                          </IconButton>
                        </InputAdornment>
                      }
                      label={`${intl.formatMessage({ id: "current password" })}*`}
                      key={`${aesf.config_form[index].name}_${aesfIndex}`}
                      value={actualValue.value}
                      onChange={(e) => {
                        let newServiceValues = clone(assetExternalServices);
                        let newIndex = assetExternalServices.findIndex(
                          (aes) => aes.ref === aesf.config_form[index].name,
                        );
                        newServiceValues[newIndex].value = e.target.value;
                        const isValid = validatePassword(e.target.value);
                        setErrors(!isValid ? "password.verification.error.message" : null);
                        setAssetExternalServices(newServiceValues);
                      }}
                    />
                    {!isNil(errors[aesf.config_form[index].name]) && (
                      <ErrorText>{intl.formatMessage({ id: errors[aesf.config_form[index].name] })}</ErrorText>
                    )}
                  </>,
                );
              }
              if (aesf.config_form[index].type === "select") {
                const actualValue = assetExternalServices.find((aes) => aes.ref === aesf.config_form[index].name);

                elements.push(
                  <>
                    <TextField
                      id={`type${index}`}
                      key={`${aesf.config_form[index].name}_${aesfIndex}`}
                      margin="dense"
                      style={{ width: "15rem" }}
                      variant="outlined"
                      value={actualValue.value}
                      onChange={(e) => {
                        let newServiceValues = clone(assetExternalServices);
                        let newIndex = assetExternalServices.findIndex(
                          (aes) => aes.ref === aesf.config_form[index].name,
                        );
                        newServiceValues[newIndex].value = e.target.value;
                        setAssetExternalServices(newServiceValues);
                      }}
                      select
                      label={aesf.name + " " + intl.formatMessage({ id: aesf.config_form[index].name })}
                      required
                    >
                      {aesf.config_form[index].options.map((opt, index2) => (
                        <MenuItem key={`${aesf.config_form[index].name}_opt_${index2}`} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </>,
                );
              }
            }
            return elements;
          })}
        </div>
      </ThemeProvider>
    </div>
  );
};

export default AssetInfoForm;
